import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export const LOG_LEVEL = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
} as const;
export type LogLevel = (typeof LOG_LEVEL)[keyof typeof LOG_LEVEL];

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  private readonly enableLogging: boolean;
  private readonly logLevel: LogLevel;

  constructor() {
    this.enableLogging = !environment.production || environment.enableLogging;
    this.logLevel = (environment.logLevel as LogLevel) || LOG_LEVEL.INFO;
  }

  error(message: string, ...optionalParams: unknown[]): void {
    if (this.shouldLog(LOG_LEVEL.ERROR)) {
      console.error(`[ERROR] ${this.formatMessage(message)}`, ...optionalParams);
    }
  }

  warn(message: string, ...optionalParams: unknown[]): void {
    if (this.shouldLog(LOG_LEVEL.WARN)) {
      console.warn(`[WARN] ${this.formatMessage(message)}`, ...optionalParams);
    }
  }

  info(message: string, ...optionalParams: unknown[]): void {
    if (this.shouldLog(LOG_LEVEL.INFO)) {
      console.info(`[INFO] ${this.formatMessage(message)}`, ...optionalParams);
    }
  }

  debug(message: string, ...optionalParams: unknown[]): void {
    if (this.shouldLog(LOG_LEVEL.DEBUG)) {
      console.debug(`[DEBUG] ${this.formatMessage(message)}`, ...optionalParams);
    }
  }

  log(message: string, ...optionalParams: unknown[]): void {
    if (this.shouldLog(LOG_LEVEL.INFO)) {
      console.log(`[LOG] ${this.formatMessage(message)}`, ...optionalParams);
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return this.enableLogging && level <= this.logLevel;
  }

  private formatMessage(message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] ${message}`;
  }
}
